<template>
  <a
    :href="listingUrl"
    class="relative block peer group"
    :title="`${listing.title} by ${listing.artist.full_name} in ${listing.dimensions_short}, ${listing.medium.title}`"
  >
    <div class="sold-ribbon-container uppercase text-white" v-if="soldRibbon && listing.sold">
      <div class="sold-ribbon">Sold</div>
    </div>

    <div
      class="bg-gray-100 overflow-hidden"
      :style="`aspect-ratio: auto ${listing.images.primary_attachment_aspect_ratio};`"
    >
      <div
        v-if="listing.reprint && !listing.sold"
        class="absolute z-[2] top-5 left-5 py-2 px-4 flex items-center space-x-2 text-bt-blue-500 bg-white rounded-full shadow"
      >
        <FaIcon :icon="['far', 'image']" />
        <span class="font-semibold">Print</span>
      </div>
      <img
        v-intersect.once="loadFullImage"
        class="mx-auto w-full h-full object-cover transition-transform ease-in-out duration-700 group-hover:scale-110"
        :id="'ListingImg__' + listing.id"
        :src="imageSrc"
        :srcset="fullSrcset"
        sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
        :alt="`${listing.title} by ${listing.artist.full_name} in ${listing.dimensions_short}, ${listing.medium.title}`"
        @error="fullSrcset ? handleFullError() : handleTinyError()"
      />
    </div>
    <div class="px-4 py-6">
      <div class="flex items-center">
        <strong class="font-normal text-sm uppercase mr-3 text-zinc-900 capitalize line-clamp-1">
          {{ listing.title | truncate(29, "...") }}
        </strong>

        <div class="ml-auto flex items-center">
          <Favourite :listing="listing" :guestFavourites="guestFavourites" />
        </div>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-zinc-400 text-sm capitalize line-clamp-1">
          {{ listing.artist.full_name }}
        </span>
      </div>

      <div class="flex flex-row flex-wrap items-start text-zinc-400 text-xs mt-4">
        <template v-if="listing.reprint">
          <p>
            Print on Canvas,
            <template v-if="listing.reprint"> Multiple Sizes </template>
            <template v-else>
              {{ listing.dimensions_short }}
            </template>
          </p>
        </template>
        <template v-else>
          <p>
            <Medium :listing="listing" class="inline" />
            <span v-if="listing.frame">, Framed by Artist</span>
          </p>
          <p>, {{ listing.dimensions_short }}</p>
        </template>
      </div>

      <div class="justify-between flex items-center mt-5">
        <span class="text-black text-sm font-medium">
          <span>{{ priceLabel }}</span>
          <span class="text-xs mt-2 ml-2" v-if="listing.price_with_discount">
            <span class="font-normal line-through text-zinc-400 mr-4">{{ listing.price.format }}</span>
            <span class="bg-red-50 text-red-900 p-2 rounded-md">{{ listing.discount_percentage | discountLabel }}</span>
          </span>
          <div class="mt-2" v-if="listing.available_for_framing">
            {{ listing.price_with_framing.format }}
            <span class="font-normal text-zinc-400 ml-2">+ Frame</span>
          </div>
        </span>
      </div>

      <div v-if="userConfig.isAdmin" class="justify-between flex flex-col mt-1">
        <div>{{ listing.gp1k }} gp1k</div>

        <div v-if="shortlistVisible" class="text-xs">
          <a href="#" @click.prevent="showShortlistModal = true">+ Shortlist</a>
        </div>

        <ShortlistModal
          v-if="showShortlistModal"
          @close="showShortlistModal = false"
          :listingId="listing.id"
          :apiUrl="listing.competition_results_api_url"
        />
      </div>
    </div>
  </a>
</template>

<script>
import Favourite from "./Favourite.vue"
import ShortlistModal from "@/pages/Shortlist/ShortlistModal.vue"
import Medium from "./Medium.vue"

export default {
  name: "Listing.Item",
  data() {
    return {
      inCart: false,
      showShortlistModal: false,
      imageSrc: this.listing.images.artworks.tiny,
      fullSrcset: null,
    }
  },
  watch: {
    "listing.id"() {
      this.imageSrc = this.listing.images.artworks.tiny
      this.fullSrcset = null
    },
  },
  props: {
    soldRibbon: {
      type: Boolean,
      required: false,
      default: true,
    },
    listing: {
      type: Object,
      required: true,
      default: {},
    },
    priceFilterApplied: {
      type: Boolean,
      default: false,
    },
    sizeFilterApplied: {
      type: Boolean,
      default: false,
    },
    guestFavourites: {
      type: Array,
      required: false,
      default: [],
    },
  },
  mounted() {
    this.inCart = this.listing.in_cart
  },
  methods: {
    addToCart() {
      this.inCart = true
    },
    loadFullImage() {
      // Image names reflect grid slot width, not device type:
      //   desktop (295w) → smallest file, used on mobile/tablet (small slots, DPR=1)
      //   tablet  (353w) → mid-size file, used on desktop (320px slots)
      //   mobile  (450w) → largest file, used on large desktops + Retina devices
      // sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" matches the
      // 4-col / 3-col / 2-col grid layout so the browser picks the right file automatically.
      const { mobile, tablet, desktop } = this.listing.images.artworks
      this.fullSrcset = `${mobile} 450w, ${tablet} 353w, ${desktop} 295w`
      this.imageSrc = mobile
    },
    handleTinyError() {
      window.posthog?.capture("listing_image_error", {
        type: "tiny",
        listing_id: this.listing.id,
        url: this.listing.images.artworks.tiny,
      })
    },
    handleFullError() {
      window.posthog?.capture("listing_image_error", {
        type: "full",
        listing_id: this.listing.id,
        url: this.imageSrc,
      })
      if (!this._fullRetried) {
        this._fullRetried = true
        setTimeout(() => {
          const { mobile, tablet, desktop } = this.listing.images.artworks
          this.imageSrc = mobile + "?r=1"
          this.fullSrcset = `${mobile}?r=1 450w, ${tablet}?r=1 353w, ${desktop}?r=1 295w`
        }, 3000)
      }
    },
  },
  computed: {
    shortlistVisible() {
      return this.listing.competition_category
    },
    availabilityMode() {
      if (!this.listing.reprint && (this.listing.dispatch_from_date || this.listing.artist.holiday_mode)) {
        return "modal"
      }
    },
    listingUrl() {
      if (this.listing.reprint && !this.listing.sold && !this.priceFilterApplied && !this.sizeFilterApplied) {
        return this.listing.url
      }
      return this.listing.main_listing_result_url
    },
    priceLabel() {
      const price = (this.listing.price_with_discount || this.listing.price).format

      if (this.listing.reprint) return `from ${price}`

      return price
    },
  },
  components: {
    Medium,
    Favourite,
    ShortlistModal,
  },
}
</script>
